import { useCallback, useEffect, useRef, useState } from 'react';

import { AUTH_API, AUTH_DISABLED, emailAllowed } from '../config';

const STORAGE_KEY = 'ark-auth';

export interface AuthUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  is_superadmin: boolean;
}

const STUB_USER: AuthUser = {
  id: 'local',
  email: 'local@issue',
  first_name: 'Local',
  last_name: 'User',
  is_superadmin: true,
};

interface StoredAuth {
  access_token: string;
  refresh_token: string;
  expires_at: number;
}

function loadStored(): StoredAuth | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveTokens(accessToken: string, refreshToken: string, expiresIn: number) {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      access_token: accessToken,
      refresh_token: refreshToken,
      expires_at: Date.now() + expiresIn * 1000,
    })
  );
}

function clearStored() {
  localStorage.removeItem(STORAGE_KEY);
}

export function getAccessToken(): string | null {
  if (AUTH_DISABLED) return null;
  const stored = loadStored();
  return stored?.access_token ?? null;
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(AUTH_DISABLED ? STUB_USER : null);
  const [loading, setLoading] = useState(!AUTH_DISABLED);
  const [error, setError] = useState('');
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleRefresh = useCallback((expiresAt: number) => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    const delay = Math.max(0, expiresAt - Date.now() - 60_000);
    refreshTimer.current = setTimeout(async () => {
      const stored = loadStored();
      if (!stored) return;
      try {
        const res = await fetch(`${AUTH_API}/api/v1/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: stored.refresh_token }),
        });
        if (!res.ok) {
          clearStored();
          setUser(null);
          return;
        }
        const data = await res.json();
        saveTokens(data.access_token, data.refresh_token, data.expires_in);
        scheduleRefresh(Date.now() + data.expires_in * 1000);
      } catch {
        clearStored();
        setUser(null);
      }
    }, delay);
  }, []);

  const fetchMe = useCallback(async (accessToken: string): Promise<AuthUser | null> => {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);
      const res = await fetch(`${AUTH_API}/api/v1/auth/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        signal: controller.signal,
      });
      clearTimeout(timeout);
      if (!res.ok) return null;
      const u: AuthUser = await res.json();
      if (!emailAllowed(u.email)) {
        clearStored();
        return null;
      }
      return u;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (AUTH_DISABLED) return;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (window.location.pathname === '/auth/google/callback' && code) {
      setLoading(true);
      const redirectUri = `${window.location.origin}/auth/google/callback`;
      fetch(`${AUTH_API}/api/v1/auth/google/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, redirect_uri: redirectUri }),
      })
        .then(async (res) => {
          if (!res.ok) throw new Error('Login failed');
          const data = await res.json();
          if (data.user && !emailAllowed(data.user.email)) {
            setError('Zugriff verweigert. Kontaktiere den Administrator.');
            return;
          }
          saveTokens(data.access_token, data.refresh_token, data.expires_in);
          setUser(data.user);
          scheduleRefresh(Date.now() + data.expires_in * 1000);
        })
        .catch(() => setError('Google Login fehlgeschlagen'))
        .finally(() => {
          window.history.replaceState({}, '', '/');
          setLoading(false);
        });
      return;
    }

    const stored = loadStored();
    if (!stored) {
      setLoading(false);
      return;
    }

    if (stored.expires_at > Date.now()) {
      fetchMe(stored.access_token).then((u) => {
        if (u) {
          setUser(u);
          scheduleRefresh(stored.expires_at);
        } else {
          clearStored();
        }
        setLoading(false);
      });
    } else {
      fetch(`${AUTH_API}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: stored.refresh_token }),
      })
        .then(async (res) => {
          if (!res.ok) throw new Error();
          const data = await res.json();
          saveTokens(data.access_token, data.refresh_token, data.expires_in);
          const u = await fetchMe(data.access_token);
          if (u) {
            setUser(u);
            scheduleRefresh(Date.now() + data.expires_in * 1000);
          } else {
            clearStored();
          }
        })
        .catch(() => clearStored())
        .finally(() => setLoading(false));
    }

    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [fetchMe, scheduleRefresh]);

  const loginEmail = useCallback(async (email: string, password: string) => {
    setError('');
    try {
      const res = await fetch(`${AUTH_API}/api/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        setError(res.status === 401 || res.status === 403
          ? 'E-Mail oder Passwort ist falsch.'
          : `Login fehlgeschlagen (HTTP ${res.status})`);
        return;
      }
      const data = await res.json();
      if (data.user && !emailAllowed(data.user.email)) {
        setError('Zugriff verweigert. Kontaktiere den Administrator.');
        return;
      }
      saveTokens(data.access_token, data.refresh_token, data.expires_in);
      setUser(data.user);
      scheduleRefresh(Date.now() + data.expires_in * 1000);
    } catch {
      setError('Verbindung fehlgeschlagen');
    }
  }, [scheduleRefresh]);

  const startGoogleLogin = useCallback(async () => {
    try {
      const redirectUri = `${window.location.origin}/auth/google/callback`;
      const res = await fetch(
        `${AUTH_API}/api/v1/auth/google/authorize?redirect_uri=${encodeURIComponent(redirectUri)}`
      );
      if (!res.ok) {
        setError('Google Login nicht verfügbar');
        return;
      }
      const { url } = await res.json();
      window.location.href = url;
    } catch {
      setError('Verbindung fehlgeschlagen');
    }
  }, []);

  const logout = useCallback(() => {
    if (AUTH_DISABLED) return;
    const stored = loadStored();
    if (stored) {
      fetch(`${AUTH_API}/api/v1/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: stored.refresh_token }),
      }).catch(() => {});
    }
    clearStored();
    setUser(null);
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
  }, []);

  return { user, loading, error, logout, startGoogleLogin, loginEmail };
}
